import { create } from 'zustand';
import { api } from '@/api/client';

/**
 * Server-derived UI capabilities for the Project Dashboard. Each flag mirrors a
 * backend authorization boundary; gate a dashboard section's render (and thus its
 * mount-time fetch) on the matching flag so the UI never fires a call the server
 * would 403. Kept in sync with `deriveDashboardCapabilities` in the API's store.ts.
 */
export interface DashboardCapabilities {
  photosRead: boolean;
  notesWrite: boolean;
  planningWrite: boolean;
  vorderingRead: boolean;
  activityRead: boolean;
  chatterRead: boolean;
  offerteRead: boolean;
}

interface MergedPermissions {
  views: string[];
  tabs: string[];
  features: string[];
  /** True only for onderaannemers (subcontractors); gates the "My work" tab. */
  isOa?: boolean;
  capabilities?: {
    dashboard?: Partial<DashboardCapabilities>;
  };
}

interface PermissionsState {
  permissions: MergedPermissions | null;
  loading: boolean;
  clearPermissions: () => void;
  fetchPermissions: () => Promise<void>;
  canView: (view: string) => boolean;
  canTab: (tab: string) => boolean;
  canFeature: (feature: string) => boolean;
  /** Dashboard section capability gate. Fail-closed: false until perms load. */
  dashboardCap: (cap: keyof DashboardCapabilities) => boolean;
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  permissions: null,
  loading: false,

  clearPermissions: () => set({ permissions: null }),

  fetchPermissions: async () => {
    set({ loading: true });
    try {
      const data = await api<MergedPermissions>('/api/permissions');
      set({ permissions: data, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  canView: (view: string) => {
    const { permissions } = get();
    if (!permissions) return false;
    return permissions.views.includes(view);
  },

  canTab: (tab: string) => {
    const { permissions } = get();
    if (!permissions) return false;
    return permissions.tabs.includes(tab);
  },

  canFeature: (feature: string) => {
    const { permissions } = get();
    if (!permissions) return false;
    return permissions.features.includes(feature);
  },

  dashboardCap: (cap: keyof DashboardCapabilities) => {
    const { permissions } = get();
    return permissions?.capabilities?.dashboard?.[cap] === true;
  },
}));
